/**
 * Web应用特定的翻译缓存钩子
 *
 * 扩展核心缓存功能，添加Web应用特定的状态管理和数据处理
 */

import { useState, useCallback } from 'react';
// import { useTranslationCache } from '@readsavor/core'; // REMOVED - No longer directly uses core TranslationCache hook
import { parseTranslationResponse } from '@readsavor/utils';
import { TranslationResponse, TextSelectionTranslation, SelectionData } from '@readsavor/types'; // Added TextSelectionTranslation
import { useErrorHandler } from '@readsavor/core';
import { useWebActiveDocTranslationContext } from '../contexts/WebTranslationHistoryProvider.js'; // IMPORT NEW CONTEXT HOOK

/**
 * Web应用翻译缓存钩子返回值接口
 */
interface UseWebTranslationCacheReturn {
  /**
   * 当前选择是否存在于缓存中
   */
  isSelectionCached: boolean;

  /**
   * 检查并更新缓存状态
   */
  checkAndUpdateCacheStatus: (selection: SelectionData | null) => void;

  /**
   * 获取并准备缓存项
   */
  fetchAndPrepareCachedItem: (selection: SelectionData) => Promise<{
    originalText: string;
    translatedText: string;
    analysis?: TranslationResponse['analysis'];
  } | null>;

  // saveTranslationResult is REMOVED
}

/**
 * Web应用特定的翻译缓存钩子
 */
export function useWebTranslationCache(): UseWebTranslationCacheReturn {
  const webActiveDocCtx = useWebActiveDocTranslationContext();
  const [isSelectionCached, setIsSelectionCached] = useState(false);
  const errorHandler = useErrorHandler();

  const checkAndUpdateCacheStatus = useCallback(
    (selection: SelectionData | null) => {
      if (!webActiveDocCtx.activeIdentity || !selection) {
        setIsSelectionCached(false);
        return;
      }
      try {
        const cachedItem = webActiveDocCtx.findInCurrentDocCache(selection);
        const isCached = !!cachedItem;

        setIsSelectionCached(isCached);
      } catch (error) {
        errorHandler.handleError(error instanceof Error ? error : String(error), {
          component: 'useWebTranslationCache',
          operation: 'checkAndUpdateCacheStatus',
        });
        setIsSelectionCached(false);
      }
    },
    [webActiveDocCtx, errorHandler]
  );

  const fetchAndPrepareCachedItem = useCallback(
    async (
      selection: SelectionData
    ): Promise<{
      originalText: string;
      translatedText: string;
      analysis?: TranslationResponse['analysis'];
    } | null> => {
      if (!webActiveDocCtx.activeIdentity) {
        return null;
      }
      try {
        const cachedItem: TextSelectionTranslation | null =
          webActiveDocCtx.findInCurrentDocCache(selection);

        if (cachedItem) {
          const parsed = parseTranslationResponse(cachedItem);
          return {
            originalText: cachedItem.originalText,
            translatedText: parsed.translatedText,
            analysis: parsed.analysis,
          };
        }
        return null;
      } catch (error) {
        errorHandler.handleError(error instanceof Error ? error : String(error), {
          component: 'useWebTranslationCache',
          operation: 'fetchAndPrepareCachedItem',
        });
        return null;
      }
    },
    [webActiveDocCtx, errorHandler]
  );

  // saveTranslationResult function is REMOVED

  return {
    isSelectionCached,
    checkAndUpdateCacheStatus,
    fetchAndPrepareCachedItem,
    // saveTranslationResult, // REMOVED
  };
}

export default useWebTranslationCache;
